import { useCallback, useState } from 'react'
import { encode } from 'bs58'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useGoogleLogin } from '@react-oauth/google'
import { loginWithGoogle, loginWithPhantom } from 'redux-thunk/thunk/auth.thunk'
import config from 'config/app.config'
import { ROUTE_PATHS } from 'constants/index'
import { openErrorToaster, openInfoToaster } from 'helpers/toaster.helpers'
import { getMetaUserRecord } from 'redux-thunk/thunk/user.thunk'
import { checkUserAddress } from 'network/services/auth.service'
import { isMobile } from 'react-device-detect'

const useThirdPartyLogin = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()

  const [walletStatus, setWalletStatus] = useState(null)
  // { wallet: 'metamask' | 'phantom', status: 'failed' | 'cancelled' | 'pending' } | null

  const closeWalletStatus = () => setWalletStatus(null)

  const twitchConfigs = {
    responseType: 'code',
    clientId: config.TWITCH_CLIENT_ID,
    redirectUrl: config.LOGIN_REDIRECT_URL,
    scope: 'user:read:email'
  }

  const twitchHyperlink = `https://id.twitch.tv/oauth2/authorize?response_type=${twitchConfigs.responseType}&client_id=${twitchConfigs.clientId}&redirect_uri=${twitchConfigs.redirectUrl}&scope=${twitchConfigs.scope}`

  const getPhantomProvider = () => {
    if (window.phantom?.solana?.isPhantom) {
      return window.phantom.solana
    }
    if (window.solana?.isPhantom) {
      return window.solana
    }
    if (isMobile) {
      const originalLink = window.location.href
      window.open(`https://phantom.app/ul/browse/${originalLink}`)
      return null
    }
    window.open('https://phantom.app/', '_blank')
    return null
  }

  const requestPhantomAccount = async () => {
    const provider = getPhantomProvider()
    if (!provider) return

    setWalletStatus({ wallet: 'phantom', status: 'pending' })
    try {
      await provider.connect()
      const message = 'You are now connecting to PreTrade, – Happy playing!'
      const encodedMessage = new TextEncoder().encode(message)
      const signedMessage = await provider.signMessage(encodedMessage, 'utf8')
      if (signedMessage) {
        const action = await dispatch(loginWithPhantom({ code: encode(signedMessage.signature) }))
        if (loginWithPhantom.fulfilled.match(action)) {
          setWalletStatus(null)
        } else {
          setWalletStatus({ wallet: 'phantom', status: 'failed' })
        }
      }
    } catch (err) {
      console.log('Phantom login error: ', err)
      setWalletStatus({
        wallet: 'phantom',
        status: (err?.code === 4001 || err?.code === -32000 || err?.name === 'UserRejectedRequestError') ? 'cancelled' : 'failed'
      })
    }
  }

  const googleLogin = useGoogleLogin({
    onSuccess: (tokenResponse) => {
      dispatch(loginWithGoogle(tokenResponse))
    },
    onError: (error) => {
      console.log('error: ', error)
      openErrorToaster({ message: t('loginFailed') })
    },
    flow: 'auth-code'
  })

  const checkEthereum = () => {
    const { ethereum } = window
    if (ethereum) {
      reqMetaMaskAccount()
    } else {
      window.addEventListener('ethereum#initialized', reqMetaMaskAccount, {
        once: true
      })
      // If the event is not dispatched by the end of the timeout,
      // the user probably doesn't have MetaMask installed.
      setTimeout(reqMetaMaskAccount, 3000) // 3 seconds
    }
  }

  const handleSignMessage = async (message, accounts) => {
    const { ethereum } = window
    const provider = ethereum?.isMetaMask ? ethereum : ethereum?.providers?.find(p => p.isMetaMask) || ethereum
    const params = [message, accounts[0]]
    return await provider.request({
      method: 'personal_sign',
      params
    })
  }

  const reqMetaMaskAccount = async () => {
    // MetaMask will inject ethereum object in webpage.
    const { ethereum } = window
    const provider = ethereum?.isMetaMask ? ethereum : ethereum?.providers?.find(p => p.isMetaMask) || ethereum
    if (provider) {
      setWalletStatus({ wallet: 'metamask', status: 'pending' })
      try {
        const accounts = await provider.request({
          method: 'eth_requestAccounts'
        })
        const { nonce } = await checkUserAddress({
          ethereumAddress: accounts[0]
        })
        const message = `${config.META_MASK_SIGN_MESSAGE} \n Nonce : ${nonce}`
        const signature = await handleSignMessage(message, accounts)
        if (signature) {
          const payload = {
            ethereumAddress: accounts[0],
            signedMessage: signature
          }
          const action = await dispatch(getMetaUserRecord({ payload }))
          if (getMetaUserRecord.fulfilled.match(action)) {
            setWalletStatus(null)
          } else {
            setWalletStatus({ wallet: 'metamask', status: 'failed' })
          }
        }
      } catch (err) {
        console.log('MetaMask login error: ', err)
        setWalletStatus({
          wallet: 'metamask',
          status: (err?.code === 4001 || err?.code === -32000 || err?.name === 'UserRejectedRequestError') ? 'cancelled' : 'failed'
        })
      }
    } else if (isMobile) {
      const originalLink = window.location.href
      const linkWithoutProtocol = originalLink.replace(/^https?:\/\//, '')
      const newLink = `https://metamask.app.link/dapp/${linkWithoutProtocol}`
      window.open(newLink)
    } else {
      // in case ethereum is not available
      openInfoToaster({ message: t('metamaskExtensionNotAvailable') })
      setTimeout(() => {
        window.open('https://metamask.io/')
      }, 2000)
    }
  }

  const redirectToHome = useCallback(() => {
    history.push(ROUTE_PATHS.HOME)
  }, [])

  const redirectToProfile = useCallback(() => {
    history.push(ROUTE_PATHS.PROFILE)
  }, [])

  return {
    t,
    redirectToHome,
    checkEthereum,
    googleLogin,
    twitchHyperlink,
    requestPhantomAccount,
    redirectToProfile,
    twitchConfigs,
    walletStatus,
    closeWalletStatus
  }
}

export { useThirdPartyLogin }
